import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { isArray } from 'lodash';
import * as React from 'react';

import {
    BannerCriteriaKey,
    BannerCriteriaSetTitles,
    CampaignCriteriaKey,
    CampaignCriteriaSetTitles,
    GroupCriteriaKey,
    GroupCriteriaSetTitles
} from '../../declarations/filter/filter';
import { Bem } from '../../service/Bem/Bem';
import { serializeFilterToString } from '../../service/FilterHelpers/FilterHelpers';
import { TestBem } from '../../service/TestBem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { LinkTarget } from '../../typings/common';
import { CampaignsCalcTypeFilter } from '../../typings/domain';
import { FilterExpressionValueType, Operator } from '../../typings/filter';
import {
    GridScreenUrlSearchQueryParams,
    RowHeightsUserLogs
} from '../../typings/grid';
import {
    logBanner,
    logCampaign,
    LogEntityData,
    logGroup,
    LogNavEntityParams
} from '../../typings/log';
import {
    ExpandableCell,
    expandablePaddings,
    ExpandableProps
} from '../ExpandableCell/ExpandableCell';
import { GridCellContentOverflow } from '../GridCellContentOverflow/GridCellContentOverflow';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './LogLinksListCell.css';
import * as keyset from './LogLinksListCell.i18n';

const i18n = i18nFactory(keyset);

export interface LogLinkListProps extends ExpandableProps {
    data: Array<LogEntityData> | LogEntityData;
    cid: string;
    calcType: CampaignsCalcTypeFilter;
    login: string;
    type: logBanner | logGroup | logCampaign;
    onNameClick?: (params: LogNavEntityParams) => void;
    isGridEnabled?: boolean;
}

export class LogLinksListCell extends ExpandableCell<LogLinkListProps> {
    protected getContent() {
        const { data, isExpanded } = this.props;
        const list = isArray(data) ? data : [data];
        const linksCount = list.length;

        if (linksCount === 0) {
            return '–';
        }

        const firstLink = this.getLink(list[0], -1);
        if (linksCount === 1) {
            return (
                <div className={Bem.cls('log-links-list-cell')}>
                    <div
                        className={Bem.cls('log-links-list-cell', 'first-link')}
                        key={'banner-first'}
                    >
                        {firstLink}
                    </div>
                </div>
            );
        }

        const moreLinksCount = linksCount - 1;

        return (
            <div className={Bem.cls('log-links-list-cell')}>
                <div
                    className={classNames(
                        Bem.cls('log-links-list-cell', 'first-link'),
                        Bem.cls('log-links-list-cell', 'link')
                    )}
                    key={'link-first'}
                >
                    {firstLink}
                </div>
                <div key={'other'}>
                    <div
                        className={classNames(
                            Bem.cls('log-links-list-cell', 'more'),
                            isExpanded
                                ? Bem.cls('log-links-list-cell', 'hidden')
                                : ''
                        )}
                        key={'add-more'}
                    >
                        {i18n('and-more-links', { moreLinksCount })}
                    </div>
                    <div
                        key={'list'}
                        className={classNames(
                            Bem.cls('log-links-list-cell', 'other-links'),
                            isExpanded
                                ? ''
                                : Bem.cls('log-links-list-cell', 'hidden')
                        )}
                    >
                        {list.slice(1, linksCount).map(this.getLink)}
                    </div>
                </div>
            </div>
        );
    }

    protected getCellMaxHeight(): number {
        const { canExpand, data } = this.props;
        if (!canExpand) {
            return RowHeightsUserLogs.default;
        }
        const list = isArray(data) ? data : [data];
        return list.length * 40 + expandablePaddings;
    }

    private getLink = (data: LogEntityData, index: number): React.ReactNode => {
        const { cid, type, isGridEnabled } = this.props;
        const { id } = data;

        let title;

        switch (type) {
            case logCampaign:
                title = i18n('open-campaign', { cid });
                break;
            case logBanner:
                title = i18n('open-banner', { banner: id });
                break;
            case logGroup:
                title = i18n('open-group', { group: id });
                break;
            default:
                throw Error(
                    `Тип ${type} не поддерживается в ячейке со списком`
                );
        }

        const href = isGridEnabled
            ? this.getLinkToNewInterface(data)
            : this.getLinkToOldInterface(data);

        return (
            <div
                className={Bem.cls('log-links-list-cell', 'link')}
                key={`log-link-${index}`}
            >
                <h3 className={Bem.cls('log-links-list-cell', 'title')}>
                    <GridCellContentOverflow>
                        {this.getNameContent(data, index)}
                    </GridCellContentOverflow>

                    <StyledLink
                        key={`link-${index}`}
                        title={title}
                        theme={StyledLinkTheme.icon}
                        cls={Bem.cls('log-links-list-cell', 'outer-link')}
                        target={LinkTarget.blank}
                        href={href}
                    >
                        <Shape2
                            width={16}
                            height={16}
                            icon={Shape2Icon.link2}
                        />
                    </StyledLink>
                </h3>

                <p
                    className={classNames(
                        Bem.cls('log-links-list-cell', 'id'),
                        TestBem.cls('log-links-list-cell', 'id')
                    )}
                >
                    № {data.id}
                </p>
            </div>
        );
    };

    private getLinkToOldInterface = (data: LogEntityData) => {
        const { type, login, cid } = this.props;
        const { id } = data;

        let params;

        switch (type) {
            case logCampaign:
                params = {};
                break;
            case logBanner:
                params = {
                    search_by: 'num',
                    search_banner: id,
                    tab: 'all'
                };
                break;
            case logGroup:
                params = {
                    tab: 'all',
                    search_by: 'group',
                    search_banner: id
                };
                break;
            default:
                throw Error(
                    `Тип ${type} не поддерживается в ячейке со списком`
                );
        }

        return UrlHelpers.getDirectLink('showCamp', {
            ulogin: login,
            cid: cid,
            ...params
        });
    };

    private getLinkToNewInterface = (data: LogEntityData) => {
        const { type, login, cid, calcType } = this.props;
        const { id } = data;

        switch (type) {
            case logCampaign: {
                const filter = serializeFilterToString([
                    {
                        field:
                            CampaignCriteriaSetTitles[CampaignCriteriaKey.id],
                        operator: Operator.EQUALS,
                        value: {
                            type: FilterExpressionValueType.numberSet,
                            value: [cid]
                        }
                    }
                ]);

                return UrlHelpers.getCampaignListUrl({
                    [GridScreenUrlSearchQueryParams.calcType]: calcType,
                    [GridScreenUrlSearchQueryParams.filter]: filter,
                    [GridScreenUrlSearchQueryParams.ulogin]: login
                });
            }
            case logBanner: {
                const filter = serializeFilterToString([
                    {
                        field: BannerCriteriaSetTitles[BannerCriteriaKey.id],
                        operator: Operator.EQUALS,
                        value: {
                            type: FilterExpressionValueType.numberSet,
                            value: [id]
                        }
                    }
                ]);

                return UrlHelpers.getBannersListUrl({
                    [GridScreenUrlSearchQueryParams.campaignsIds]: [cid],
                    [GridScreenUrlSearchQueryParams.calcType]: calcType,
                    [GridScreenUrlSearchQueryParams.filter]: filter,
                    [GridScreenUrlSearchQueryParams.ulogin]: login
                });
            }
            case logGroup: {
                const filter = serializeFilterToString([
                    {
                        field: GroupCriteriaSetTitles[GroupCriteriaKey.id],
                        operator: Operator.EQUALS,
                        value: {
                            type: FilterExpressionValueType.numberSet,
                            value: [id]
                        }
                    }
                ]);

                return UrlHelpers.getGroupsListUrl({
                    [GridScreenUrlSearchQueryParams.campaignsIds]: [cid],
                    [GridScreenUrlSearchQueryParams.calcType]: calcType,
                    [GridScreenUrlSearchQueryParams.filter]: filter,
                    [GridScreenUrlSearchQueryParams.ulogin]: login
                });
            }
            default:
                throw Error(
                    `Тип ${type} не поддерживается в ячейке со списком`
                );
        }
    };

    private getNameContent = (
        data: LogEntityData,
        index: number
    ): React.ReactNode | string => {
        const { id, name } = data;
        const { type } = this.props;
        let title, params;

        switch (type) {
            case logCampaign:
                params = { cid: id };
                title = i18n('filter-by-cid', params);
                break;
            case logGroup:
                params = { group: id };
                title = i18n('filter-by-group', params);
                break;
            case logBanner:
                params = { banner: id };
                title = i18n('filter-by-banner', params);
                break;
            default:
                throw Error(`Неизвестный тип ${type}`);
        }

        if (id) {
            return (
                <StyledLink
                    key={`nav-${index}`}
                    title={title}
                    theme={StyledLinkTheme.common}
                    cls={Bem.cls('log-links-list-cell', 'filter-link')}
                    onClick={this.onItemClick(params)}
                >
                    {name}
                </StyledLink>
            );
        }

        return name;
    };

    private onItemClick = (params: LogNavEntityParams) => () => {
        const { onNameClick } = this.props;
        if (onNameClick) {
            onNameClick(params);
        }
    };
}
